/**
 * Picks which source the Table Explorer grid renders rows and columns from.
 *
 * Rows and columns must always come from the SAME source: the grid drops its
 * skeleton the moment `rows` is non-empty, so a phase with rows but no columns
 * paints a column-less grid.
 */

/** The in-flight streamed preview, as reported by `usePopulationProgress`. */
export type StreamedPreview = {
  /**
   * Terminal states arrive BEFORE the fetch resolves — `executeQueryWithCache`
   * sends the `'ready'` progress event, then awaits the fast-path decision and
   * ships the full payload over IPC. Never gate the preview on this.
   */
  readonly state: 'populating' | 'ready' | 'failed' | 'cancelled';
  readonly mode: 'inline' | 'windowed';
  readonly rowCount: number;
  readonly columnCount: number;
};

export type TableExplorerDisplaySource =
  /** `populationProgress.rows` + its streamed columns. */
  | 'streamed'
  /** The persisted `tabContent.rows` + `tabContent.columns`. */
  | 'persisted'
  /** The settled query result, before the store round-trip has committed it. */
  | 'settled'
  /** Nothing to show — skeleton, laid out against the previously persisted columns. */
  | 'pending';

/**
 * Pick the source for the current phase.
 *
 * Note the union above has deliberately NO `'windowed'` member. A page that
 * crosses the result-cache promotion thresholds has its rows parked in a
 * temp-SQLite source instead of coming back inline, but `useTableData` reads
 * that one page back out of the cache inside its own `queryFn` (see
 * `resolveFetchedRows`) — so by the time the fetch settles, a windowed page is
 * an ordinary settled result whose rows and columns describe the same fetch.
 * Windowed mode changes what the page may DO (read-only, `WindowedModeBanner`),
 * never where the grid reads it from, and this ladder stays a pure function of
 * "what do we have to paint right now".
 */
export function selectTableExplorerDisplaySource(args: {
  readonly isFetching: boolean;
  readonly streamed?: StreamedPreview;
  readonly persistedRowCount: number;
  readonly hasSettledData: boolean;
}): TableExplorerDisplaySource {
  const { isFetching, streamed, persistedRowCount, hasSettledData } = args;

  if (isFetching) {
    // A promoted (windowed) population keeps `rows` empty on purpose:
    // `usePopulationProgress` drops its inline accumulator the moment an event
    // arrives with `mode: 'windowed'`, because those rows now live in the cache
    // source. There is nothing to preview until the fetch settles and
    // `resolveFetchedRows` has read the page back.
    const usablePreview =
      streamed !== undefined &&
      streamed.mode === 'inline' &&
      streamed.rowCount > 0 &&
      streamed.columnCount > 0;
    return usablePreview ? 'streamed' : 'pending';
  }

  if (persistedRowCount > 0) return 'persisted';
  if (hasSettledData) return 'settled';
  return 'pending';
}
